/**
 * Number input that keeps a free text draft while typing (empty / partial ok)
 * and commits a parsed value only on blur.
 */
import { TextField, type TextFieldProps } from '@mui/material';
import React, { useEffect, useState } from 'react';

export type CommitNumberFieldProps = Omit<
  TextFieldProps,
  'value' | 'onChange' | 'type'
> & {
  value: number | null | undefined | '';
  /** Called on blur with a finite number, or null when cleared (if allowEmpty). */
  onCommit: (value: number | null) => void;
  /** When true, empty blur commits null. When false, empty blur keeps the last committed value. */
  allowEmpty?: boolean;
  /** Optional transform after parse (e.g. round to 3 dp). */
  formatCommitted?: (n: number) => number;
};

function toDraft(value: number | null | undefined | ''): string {
  if (value === null || value === undefined || value === '') return '';
  return String(value);
}

export const CommitNumberField: React.FC<CommitNumberFieldProps> = ({
  value,
  onCommit,
  allowEmpty = true,
  formatCommitted,
  disabled,
  onClick,
  onFocus,
  onBlur,
  onKeyDown,
  inputProps,
  ...rest
}) => {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(() => toDraft(value));

  useEffect(() => {
    if (!focused) setDraft(toDraft(value));
  }, [value, focused]);

  const display = focused ? draft : toDraft(value);

  const commit = () => {
    setFocused(false);
    const raw = draft.trim();
    if (raw === '' || raw === '-' || raw === '.' || raw === '-.') {
      if (allowEmpty) {
        onCommit(null);
      } else {
        // Restore display from committed value; do not invent a number.
        setDraft(toDraft(value));
      }
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      setDraft(toDraft(value));
      return;
    }
    const next = formatCommitted ? formatCommitted(n) : n;
    onCommit(next);
    setDraft(toDraft(next));
  };

  return (
    <TextField
      {...rest}
      type="text"
      disabled={disabled}
      value={display}
      onClick={onClick}
      onFocus={(e) => {
        setFocused(true);
        setDraft(toDraft(value));
        onFocus?.(e);
      }}
      onChange={(e) => {
        // Allow digits, sign, decimal point, empty — no live "repair"
        setDraft(e.target.value);
      }}
      onBlur={(e) => {
        commit();
        onBlur?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          (e.target as HTMLInputElement).blur();
        }
        onKeyDown?.(e);
      }}
      inputProps={{
        ...inputProps,
        inputMode: 'decimal',
      }}
    />
  );
};
